// src/CleanTruck.jsx
import React, { useEffect, useState } from 'react';
import { signInWithRedirect } from 'aws-amplify/auth';
import { useSession } from './auth/SessionProvider.jsx';
import { api } from './lib/apiFetch';

import CleanList from './CleanList';
import CleanForm from './CleanForm';
import PendingTrucks from './PendingTrucks';

// Relative path; api helper adds REACT_APP_API_BASE and Authorization
const DETAILING_PATH = '/detailing';

// Helpers to keep logs consistent & compact
const logGroup = (label, fn) => {
  try {
    console.groupCollapsed(label);
    fn();
  } finally {
    console.groupEnd();
  }
};
const nowIso = () => new Date().toISOString();

export default function CleanTruck() {
  const { signedIn } = useSession();

  const [cleaningLists, setCleaningLists] = useState({
    currentWeek: { startDate: null, endDate: null, list: [] },
    nextWeek: { startDate: null, endDate: null, list: [] },
    cleaningStaff: [],
    pendingTrucks: [],
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [forceRerender, setForceRerender] = useState(false);

  // Log staff whenever it changes (fixes the out-of-scope console.log)
  useEffect(() => {
    const staff = cleaningLists.cleaningStaff;
    logGroup('[CleanTruck] Staff snapshot', () => {
      console.info('time:', nowIso());
      console.info('count:', Array.isArray(staff) ? staff.length : 'n/a');
      console.info('sample:', Array.isArray(staff) ? staff[0] : undefined);
    });
  }, [cleaningLists.cleaningStaff]);

  // --- Debug fallback: if api.get throws, fetch directly to inspect status/raw body ---
  async function debugProbeDirect() {
    const base = process.env.REACT_APP_API_BASE || '(missing REACT_APP_API_BASE)';
    const url = `${base}${DETAILING_PATH}?view=full`;

    logGroup('[CleanTruck][DebugProbe] Direct fetch (no Authorization, no Content-Type)', () => {
      console.info('time:', nowIso());
      console.info('url:', url);
      console.info('origin:', window.location.origin);
    });

    try {
      // No headers => no preflight for GET
      const res = await fetch(url, { method: 'GET' });
      const raw = await res.text();
      logGroup('[CleanTruck][DebugProbe] Response', () => {
        console.info('status:', res.status, res.statusText);
        console.info('ok:', res.ok);
        console.info('content-type:', res.headers.get('content-type'));
        console.info('rawBody (first 800 chars):', raw?.slice(0, 800));
        const cors = {
          allowOrigin: res.headers.get('access-control-allow-origin'),
          allowMethods: res.headers.get('access-control-allow-methods'),
          allowHeaders: res.headers.get('access-control-allow-headers'),
        };
        console.info('CORS headers:', cors);
      });
    } catch (probeErr) {
      console.error('[CleanTruck][DebugProbe] fetch failed:', probeErr);
    }
  }

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!signedIn) {
        logGroup('[CleanTruck] Skipping load (not signed in)', () => {
          console.info('time:', nowIso());
          console.info('signedIn:', signedIn);
        });
        return;
      }

      setLoading(true);
      setError('');

      const base = process.env.REACT_APP_API_BASE || '(missing REACT_APP_API_BASE)';
      const rel = `${DETAILING_PATH}?view=full`;

      logGroup('[CleanTruck] Begin fetch', () => {
        console.info('time:', nowIso());
        console.info('signedIn:', signedIn);
        console.info('REACT_APP_API_BASE:', base);
        console.info('relative path:', rel);
        console.info('final URL:', `${base}${rel}`);
        console.info('expect JSON shape: { success, cleaningLists, cleaningStaff, pendingTrucks }');
      });

      try {
        const t0 = performance.now?.() ?? Date.now();
        const responseData = await api.get(rel); // api helper adds base + Authorization
        const t1 = performance.now?.() ?? Date.now();

        if (cancelled) return;

        logGroup('[CleanTruck] api.get() success', () => {
          console.info('durationMs:', Math.round(t1 - t0));
          const keys = responseData ? Object.keys(responseData) : null;
          console.info('top-level keys:', keys);
          console.info('success flag:', responseData?.success);
        });

        if (responseData?.success) {
          const { cleaningLists: cl, cleaningStaff, pendingTrucks } = responseData;

          const filterInactive = (list = []) =>
            (Array.isArray(list) ? list : []).filter((truck) => truck.unitStatus !== 'I');

          setCleaningLists({
            currentWeek: {
              startDate: cl?.currentWeek?.startDate ?? null,
              endDate: cl?.currentWeek?.endDate ?? null,
              list: filterInactive(cl?.currentWeek?.list ?? []),
            },
            nextWeek: {
              startDate: cl?.nextWeek?.startDate ?? null,
              endDate: cl?.nextWeek?.endDate ?? null,
              list: filterInactive(cl?.nextWeek?.list ?? []),
            },
            cleaningStaff: Array.isArray(cleaningStaff) ? cleaningStaff : [],
            pendingTrucks: filterInactive(pendingTrucks ?? []),
          });
        } else {
          const msg = responseData?.message || 'Failed to fetch cleaning lists.';
          logGroup('[CleanTruck] api.get() returned success=false', () => {
            console.warn('message:', msg);
            console.info('payload preview:', JSON.stringify(responseData)?.slice(0, 800));
          });
          setError(msg);
          setCleaningLists({
            currentWeek: { startDate: null, endDate: null, list: [] },
            nextWeek: { startDate: null, endDate: null, list: [] },
            cleaningStaff: [],
            pendingTrucks: [],
          });
        }
      } catch (e) {
        if (cancelled) return;

        const message = String(e?.message || e);
        logGroup('[CleanTruck] api.get() threw', () => {
          console.error('error:', e);
          console.info('error.message:', message);
          console.info('about to run DebugProbe (no Authorization)…');
        });

        setError(message);
        setCleaningLists({
          currentWeek: { startDate: null, endDate: null, list: [] },
          nextWeek: { startDate: null, endDate: null, list: [] },
          cleaningStaff: [],
          pendingTrucks: [],
        });

        debugProbeDirect(); // fire-and-forget
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [signedIn, forceRerender]);

  const handleFormSubmission = () => {
    setForceRerender((prev) => !prev);
  };

  return (
    <div>
      <section id="Title" className="formTitle colored-bg">
        <div className="formHead">
          <h3>Truck Detailing Information</h3>
        </div>
      </section>

      {!signedIn && (
        <section className="colored-bg" style={{ padding: 16 }}>
          <div style={{ color: 'crimson', marginBottom: 8 }}>
            You must sign in to view and submit detailing information.
          </div>
          <button className="link-btn" onClick={() => signInWithRedirect()}>
            Sign in
          </button>
        </section>
      )}

      {error && (
        <section className="colored-bg" style={{ padding: 16 }}>
          <div style={{ color: 'crimson' }}>{error}</div>
        </section>
      )}

      <section id="main" className="colored-bg">
        <div className="container left">
          <div className="container listDiv">
            <h4 className="listHead">
              Current week: {cleaningLists.currentWeek?.startDate || '—'} to {cleaningLists.currentWeek?.endDate || '—'}
            </h4>
            {loading ? <div>Loading…</div> : <CleanList truckList={cleaningLists.currentWeek?.list || []} />}
          </div>

          <div className="container listDiv">
            <h4 className="listHead">
              Upcoming week: {cleaningLists.nextWeek?.startDate || '—'} to {cleaningLists.nextWeek?.endDate || '—'}
            </h4>
            {loading ? <div>Loading…</div> : <CleanList truckList={cleaningLists.nextWeek?.list || []} />}
          </div>
        </div>

        <div className="container formDiv center listDiv">
          {signedIn ? (
            <CleanForm data={cleaningLists.cleaningStaff} onFormSubmit={handleFormSubmission} />
          ) : (
            <div style={{ opacity: 0.6 }}>Sign in to submit cleaning forms.</div>
          )}
        </div>

        <div className="container">
          <div className="container listDiv">
            <h4 className="listHead">Completed cleanings awaiting payout:</h4>
            {loading ? (
              <div>Loading…</div>
            ) : (
              <PendingTrucks className="listDiv" truckList={cleaningLists.pendingTrucks} />
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
